import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Shield, QrCode, FileText, Building2, UserCheck, 
  Link2, Clock, CheckCircle2, XCircle, AlertCircle,
  Download, Share2, Plus, Eye, Loader2, CreditCard,
  Hospital, Stethoscope, Pill, Syringe, ClipboardList
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import QRCode from 'qrcode';

const RECORD_TYPES = [
  { id: 'OPConsultation', name: 'OPD Consultation', icon: Stethoscope },
  { id: 'DiagnosticReport', name: 'Lab Report', icon: ClipboardList },
  { id: 'Prescription', name: 'Prescription', icon: Pill },
  { id: 'ImmunizationRecord', name: 'Vaccination', icon: Syringe },
];

const CONSENT_PURPOSES = [
  { id: 'CareManagement', name: 'Care Management' },
  { id: 'BreakTheGlass', name: 'Emergency Access' },
  { id: 'PublicHealthSafety', name: 'Public Health' },
  { id: 'HealthcarePayment', name: 'Insurance/Payment' },
];

const ABDMIntegration = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [abhaProfile, setAbhaProfile] = useState(null);
  const [isLinked, setIsLinked] = useState(false);
  const [careContexts, setCareContexts] = useState([]);
  const [healthRecords, setHealthRecords] = useState([]);
  const [consents, setConsents] = useState([]);
  const [facilities, setFacilities] = useState([]);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  
  // Form states
  const [createForm, setCreateForm] = useState({
    mobile_number: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    address: '',
    state: '',
    district: '',
    pincode: ''
  });
  const [otpStep, setOtpStep] = useState(false);
  const [txnId, setTxnId] = useState('');
  const [otp, setOtp] = useState('');
  const [linkForm, setLinkForm] = useState({ abha_number: '', abha_address: '' });
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [profileRes, careRes, recordsRes, consentsRes, facilitiesRes] = await Promise.all([
        axios.get(`${API}/abdm/abha/profile`),
        axios.get(`${API}/abdm/hip/care-contexts`).catch(() => ({ data: { careContexts: [] } })),
        axios.get(`${API}/abdm/hiu/health-records`).catch(() => ({ data: { records: [] } })),
        axios.get(`${API}/abdm/hiu/consents`).catch(() => ({ data: { consents: [] } })),
        axios.get(`${API}/abdm/facilities`).catch(() => ({ data: { facilities: [] } }))
      ]);
      
      setIsLinked(profileRes.data.linked);
      setAbhaProfile(profileRes.data.profile);
      setCareContexts(careRes.data.careContexts || []);
      setHealthRecords(recordsRes.data.records || []);
      setConsents(consentsRes.data.consents || []);
      setFacilities(facilitiesRes.data.facilities || []);
    } catch (error) {
      console.error('Error loading ABDM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateABHA = async () => {
    if (!createForm.mobile_number || !createForm.first_name || !createForm.last_name || 
        !createForm.date_of_birth || !createForm.gender) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/abdm/abha/create/init`, createForm);
      setTxnId(response.data.txnId);
      setOtpStep(true);
      toast.success(response.data.message);
      if (response.data.hint) {
        toast.info(response.data.hint);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate ABHA creation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) {
      toast.error('Please enter OTP');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/abdm/abha/create/verify`, {
        txn_id: txnId,
        otp: otp
      });
      toast.success('ABHA created successfully!');
      setShowCreateDialog(false);
      setOtpStep(false);
      setOtp('');
      setCreateForm({
        mobile_number: '', first_name: '', last_name: '',
        date_of_birth: '', gender: '', address: '',
        state: '', district: '', pincode: ''
      });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLinkABHA = async () => {
    if (!linkForm.abha_number || !linkForm.abha_address) {
      toast.error('Please enter ABHA number and address');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/abdm/abha/link`, linkForm);
      toast.success('ABHA linked successfully!');
      setShowLinkDialog(false);
      setLinkForm({ abha_number: '', abha_address: '' });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link ABHA');
    } finally {
      setSubmitting(false);
    }
  };

  const generateQRCode = async () => {
    try {
      const response = await axios.get(`${API}/abdm/qr-code`);
      const qrString = JSON.stringify(response.data.qrData);
      const qrUrl = await QRCode.toDataURL(qrString, {
        width: 300,
        margin: 2,
        color: { dark: '#1e40af', light: '#ffffff' }
      });
      setQrCodeUrl(qrUrl);
      setShowQRDialog(true);
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };

  const handleGrantConsent = async (consentId) => {
    try {
      await axios.post(`${API}/abdm/hiu/consent/${consentId}/grant`);
      toast.success('Consent granted');
      await loadData();
    } catch (error) {
      toast.error('Failed to grant consent');
    }
  };

  const handleDenyConsent = async (consentId) => {
    try {
      await axios.post(`${API}/abdm/hiu/consent/${consentId}/deny`);
      toast.success('Consent denied');
      await loadData();
    } catch (error) {
      toast.error('Failed to deny consent');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'REQUESTED': { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      'GRANTED': { color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      'DENIED': { color: 'bg-red-100 text-red-700', icon: XCircle },
      'active': { color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      'linked': { color: 'bg-blue-100 text-blue-700', icon: Link2 },
    };
    
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-700', icon: AlertCircle };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <Navigation user={user} setUser={setUser} />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
                ABDM Integration
              </h1>
              <p className="text-gray-600">
                Ayushman Bharat Digital Mission - Your Digital Health Identity
              </p>
            </div>
          </div>
          
          {isLinked && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={generateQRCode}>
                <QrCode className="w-4 h-4 mr-2" />
                Show QR
              </Button>
              <Button className="bg-gradient-to-r from-blue-600 to-indigo-600">
                <Share2 className="w-4 h-4 mr-2" />
                Share ABHA
              </Button>
            </div>
          )}
        </div>

        {/* Not Linked State */}
        {!isLinked && (
          <Card className="mb-8 border-2 border-dashed border-blue-300">
            <CardContent className="p-8 text-center">
              <Shield className="w-16 h-16 mx-auto text-blue-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No ABHA Linked</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Create a new ABHA (Ayushman Bharat Health Account) or link your existing ABHA 
                to access India's unified health records system.
              </p>
              <div className="flex justify-center gap-4">
                <Button onClick={() => setShowCreateDialog(true)} className="bg-gradient-to-r from-blue-600 to-indigo-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Create New ABHA
                </Button>
                <Button variant="outline" onClick={() => setShowLinkDialog(true)}>
                  <Link2 className="w-4 h-4 mr-2" />
                  Link Existing ABHA
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ABHA Profile Card */}
        {isLinked && abhaProfile && (
          <Card className="mb-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                    <UserCheck className="w-8 h-8" />
                  </div>
                  <div>
                    <p className="text-blue-100 text-sm">ABHA Number</p>
                    <h2 className="text-2xl font-bold">{abhaProfile.abha_number}</h2>
                    <p className="text-blue-100">{abhaProfile.abha_address}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-blue-100 text-sm">Name</p>
                  <p className="text-xl font-semibold">{abhaProfile.full_name || abhaProfile.first_name + ' ' + abhaProfile.last_name}</p>
                  <Badge className="bg-white/20 text-white mt-2">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Tabs */}
        {isLinked && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white/80 backdrop-blur">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="records">Health Records</TabsTrigger>
              <TabsTrigger value="consents">Consents</TabsTrigger>
              <TabsTrigger value="facilities">Facilities</TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Personal Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Full Name</p>
                        <p className="font-medium">{abhaProfile?.full_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Gender</p>
                        <p className="font-medium">{abhaProfile?.gender === 'M' ? 'Male' : abhaProfile?.gender === 'F' ? 'Female' : 'Other'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Date of Birth</p>
                        <p className="font-medium">{abhaProfile?.date_of_birth || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Mobile</p>
                        <p className="font-medium">{abhaProfile?.mobile || 'N/A'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Address</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <p className="text-sm text-gray-500">Address</p>
                        <p className="font-medium">{abhaProfile?.address || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">State</p>
                        <p className="font-medium">{abhaProfile?.state || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">District</p>
                        <p className="font-medium">{abhaProfile?.district || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Pincode</p>
                        <p className="font-medium">{abhaProfile?.pincode || 'N/A'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Care Contexts */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Care Contexts</CardTitle>
                    <CardDescription>Linked health records and visits</CardDescription>
                  </div>
                  <Button size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Context
                  </Button>
                </CardHeader>
                <CardContent>
                  {careContexts.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No care contexts linked yet</p>
                  ) : (
                    <div className="space-y-3">
                      {careContexts.map((ctx, idx) => (
                        <div key={`item-${idx}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-blue-600" />
                            <div>
                              <p className="font-medium">{ctx.display}</p>
                              <p className="text-sm text-gray-500">{ctx.recordType}</p>
                            </div>
                          </div>
                          {getStatusBadge(ctx.status)}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Health Records Tab */}
            <TabsContent value="records" className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Your Health Records</h3>
                <p className="text-sm text-gray-500">{healthRecords.length} records found</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {healthRecords.map((record, idx) => {
                  const RecordIcon = RECORD_TYPES.find(r => r.id === record.record_type)?.icon || FileText;
                  return (
                    <Card key={`item-${idx}`} className="hover:shadow-lg transition-all cursor-pointer" onClick={() => setSelectedRecord(record)}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-blue-100">
                            <RecordIcon className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{record.fhir_record?.resourceType || record.record_type}</p>
                            <p className="text-sm text-gray-500">{record.fhir_record?.code?.text || record.fhir_record?.type?.text || 'Health Record'}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(record.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Eye className="w-4 h-4 text-gray-400" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {healthRecords.length === 0 && (
                <Card className="p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No health records found</p>
                </Card>
              )}
            </TabsContent>

            {/* Consents Tab */}
            <TabsContent value="consents" className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Consent Management</h3>
                <Button size="sm" onClick={() => setShowConsentDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Consent Request
                </Button>
              </div>

              {consents.length === 0 ? (
                <Card className="p-8 text-center">
                  <Shield className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No consent requests</p>
                  <p className="text-sm text-gray-400">Request access to health records from other providers</p>
                </Card>
              ) : (
                <div className="space-y-4">
                  {consents.map((consent, idx) => (
                    <Card key={`item-${idx}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-2 rounded-lg bg-purple-100">
                              <Shield className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium">{consent.purpose}</p>
                              <p className="text-sm text-gray-500">
                                {consent.hi_types?.join(', ')} • {consent.requester_name}
                              </p>
                              <p className="text-xs text-gray-400">
                                Valid: {consent.date_range?.from} to {consent.date_range?.to}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(consent.status)}
                            {consent.status === 'REQUESTED' && (
                              <div className="flex gap-2 ml-4">
                                <Button size="sm" onClick={() => handleGrantConsent(consent.id)} className="bg-green-600 hover:bg-green-700">
                                  Grant
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => handleDenyConsent(consent.id)}>
                                  Deny
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Facilities Tab */}
            <TabsContent value="facilities" className="space-y-6">
              <h3 className="text-lg font-semibold">ABDM Registered Facilities</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {facilities.map((facility, idx) => (
                  <Card key={`item-${idx}`} className="hover:shadow-lg transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-teal-100">
                          <Hospital className="w-5 h-5 text-teal-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{facility.name}</p>
                          <p className="text-sm text-gray-500">{facility.city}</p>
                          <Badge variant="outline" className="mt-2">{facility.type}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Demo Notice */}
        <Card className="mt-8 bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Demo Mode</p>
                <p className="text-sm text-amber-700">
                  This is a mock implementation of ABDM integration. To connect to real ABDM services,
                  register for sandbox credentials at <a href="https://sandbox.abdm.gov.in" target="_blank" rel="noopener noreferrer" className="underline">sandbox.abdm.gov.in</a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create ABHA Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New ABHA</DialogTitle>
            <DialogDescription>
              {otpStep ? 'Enter the OTP sent to your mobile' : 'Fill in your details to create ABHA'}
            </DialogDescription>
          </DialogHeader>
          
          {!otpStep ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input 
                    value={createForm.first_name}
                    onChange={(e) => setCreateForm({...createForm, first_name: e.target.value})}
                    placeholder="First name"
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input 
                    value={createForm.last_name}
                    onChange={(e) => setCreateForm({...createForm, last_name: e.target.value})}
                    placeholder="Last name"
                  />
                </div>
              </div>
              
              <div>
                <Label>Mobile Number *</Label>
                <Input 
                  value={createForm.mobile_number}
                  onChange={(e) => setCreateForm({...createForm, mobile_number: e.target.value})}
                  placeholder="10-digit mobile number"
                  maxLength={10}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date of Birth *</Label>
                  <Input 
                    type="date"
                    value={createForm.date_of_birth}
                    onChange={(e) => setCreateForm({...createForm, date_of_birth: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Gender *</Label>
                  <Select value={createForm.gender} onValueChange={(v) => setCreateForm({...createForm, gender: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Male</SelectItem>
                      <SelectItem value="F">Female</SelectItem>
                      <SelectItem value="O">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Address</Label>
                <Input 
                  value={createForm.address}
                  onChange={(e) => setCreateForm({...createForm, address: e.target.value})}
                  placeholder="Street address"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>State</Label>
                  <Input 
                    value={createForm.state}
                    onChange={(e) => setCreateForm({...createForm, state: e.target.value})}
                    placeholder="State"
                  />
                </div>
                <div>
                  <Label>District</Label>
                  <Input 
                    value={createForm.district}
                    onChange={(e) => setCreateForm({...createForm, district: e.target.value})}
                    placeholder="District"
                  />
                </div>
                <div>
                  <Label>Pincode</Label>
                  <Input 
                    value={createForm.pincode}
                    onChange={(e) => setCreateForm({...createForm, pincode: e.target.value})}
                    placeholder="6-digit"
                    maxLength={6}
                  />
                </div>
              </div>
              
              <Button onClick={handleCreateABHA} disabled={submitting} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Send OTP
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Enter OTP</Label>
                <Input 
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="6-digit OTP"
                  maxLength={6}
                  className="text-center text-2xl tracking-widest"
                />
                <p className="text-sm text-gray-500 mt-2">
                  For testing, use OTP: <strong>123456</strong>
                </p>
              </div>
              
              <Button onClick={handleVerifyOTP} disabled={submitting} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Verify & Create ABHA
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Link ABHA Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link Existing ABHA</DialogTitle>
            <DialogDescription>Enter your existing ABHA details</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>ABHA Number (14 digits)</Label>
              <Input 
                value={linkForm.abha_number}
                onChange={(e) => setLinkForm({...linkForm, abha_number: e.target.value})}
                placeholder="XX-XXXX-XXXX-XXXX"
                maxLength={14}
              />
            </div>
            <div>
              <Label>ABHA Address</Label>
              <Input 
                value={linkForm.abha_address}
                onChange={(e) => setLinkForm({...linkForm, abha_address: e.target.value})}
                placeholder="username@abdm"
              />
            </div>
            
            <Button onClick={handleLinkABHA} disabled={submitting} className="w-full">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Link ABHA
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">ABHA QR Code</DialogTitle>
            <DialogDescription className="text-center">
              Scan this QR code to share your ABHA details
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col items-center space-y-4">
            {qrCodeUrl && (
              <img src={qrCodeUrl} alt="ABHA QR Code" className="w-64 h-64 border rounded-lg" />
            )}
            <div className="text-center">
              <p className="font-mono text-lg">{abhaProfile?.abha_number}</p>
              <p className="text-sm text-gray-500">{abhaProfile?.abha_address}</p>
            </div>
            <Button variant="outline" className="w-full">
              <Download className="w-4 h-4 mr-2" />
              Download QR Code
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Detail Dialog */}
      <Dialog open={!!selectedRecord} onOpenChange={() => setSelectedRecord(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Health Record Details</DialogTitle>
          </DialogHeader>
          
          {selectedRecord && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Record Type</p>
                  <p className="font-medium">{selectedRecord.record_type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="font-medium">{new Date(selectedRecord.created_at).toLocaleString()}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-2">FHIR Resource</p>
                <pre className="bg-gray-100 p-4 rounded-lg text-xs overflow-x-auto">
                  {JSON.stringify(selectedRecord.fhir_record, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ABDMIntegration;
